<div class="h-full flex flex-col w-full overflow-y-auto scrollbar-transparent p-4 space-y-4">

  <!-- ── Section 1: Spark Auth ──────────────────────────────────────────── -->
  <div class="briefing-section-card">
    <h3 class="text-sm font-semibold text-primary mb-3">Spark Ad Authorization</h3>

    @if (sparkAdAuthStatus()) {
      <div class="p-3 bg-gray-50 dark:bg-white/5 rounded-lg border border-gray-200 dark:border-gray-700 mb-3 space-y-1">
        <p class="text-xs text-secondary">
          Status:
          <span class="font-medium text-primary">{{ sparkAdAuthStatus()!.data.auth_code_status }}</span>
        </p>
        @if (sparkAdAuthStatus()!.data.auth_code) {
          <p class="text-xs text-secondary font-mono truncate">
            Auth Code: {{ sparkAdAuthStatus()!.data.auth_code.substring(0, 24) }}…
          </p>
        }
        @if (sparkAdAuthStatus()!.data.auth_code_end_time) {
          <p class="text-xs text-secondary">
            Expires: {{ sparkAdAuthStatus()!.data.auth_code_end_time }}
          </p>
        }
      </div>
    } @else {
      <p class="text-xs text-secondary mb-3">No auth status loaded yet.</p>
    }

    <div class="flex gap-2 flex-wrap">
      <button (click)="applyForAuth()" type="button"
        class="rounded-sm bg-primary px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:bg-primary dark:shadow-none dark:hover:bg-primary/90 dark:focus-visible:outline-primary">
        Apply for Auth
      </button>
      <button (click)="applySparkAuthorizationCode()" type="button"
        class="rounded-sm bg-primary px-2 py-1 text-xs font-semibold text-white shadow-xs hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:bg-primary dark:shadow-none dark:hover:bg-primary/90 dark:focus-visible:outline-primary">
        Apply Auth Code
      </button>
    </div>
  </div>

  <!-- ── Section 2: Create Campaign + Spark Ad ─────────────────────────── -->
  @if (sparkAdAuthStatus()?.data?.auth_code) {
    <div class="briefing-section-card">
      <h3 class="text-sm font-semibold text-primary mb-3">Create Campaign &amp; Spark Ad</h3>

      @if (createError()) {
        <div class="mb-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg flex items-start justify-between gap-2">
          <p class="text-red-700 dark:text-red-300 text-xs">{{ createError() }}</p>
          <button type="button" (click)="createError.set(null)"
            class="flex-shrink-0 text-red-500 hover:text-red-700 dark:hover:text-red-300">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      }

      @if (createSuccess()) {
        <div class="mb-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
          <p class="text-green-700 dark:text-green-300 text-xs">Campaign and Spark Ad created successfully.</p>
        </div>
      }

      <div class="space-y-1 mb-4">
        <p class="text-xs text-secondary">
          Campaign name: <span class="font-medium text-primary">{{ manageBriefTabsService.focussedBrief()?.title }}</span>
        </p>
        <p class="text-xs text-secondary font-mono">
          Video ID: <span class="font-medium text-primary">{{ video().video_info.video_id }}</span>
        </p>
      </div>

      <!-- Advertiser account selector -->
      @if (advertiserAccounts().length > 1) {
        <div class="mb-4">
          <label class="block text-xs font-medium text-secondary mb-1">Advertiser Account</label>
          <select
            [value]="selectedAdvertiserId()"
            (change)="selectedAdvertiserId.set($any($event.target).value)"
            class="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-1.5 text-xs text-primary focus:outline-none focus:ring-2 focus:ring-[var(--primary)]">
            <option value="">Select an advertiser account…</option>
            @for (id of advertiserAccounts(); track id) {
              <option [value]="id">{{ id }}</option>
            }
          </select>
        </div>
      } @else if (advertiserAccounts().length === 1) {
        <div class="mb-4">
          <p class="text-xs text-secondary">
            Advertiser account: <span class="font-mono font-medium text-primary">{{ advertiserAccounts()[0] }}</span>
          </p>
        </div>
      }

      <div class="mb-4">
        <label class="block text-xs font-medium text-secondary mb-1">Campaign Objective</label>
        <select
          [value]="selectedObjectiveType()"
          (change)="selectedObjectiveType.set($any($event.target).value)"
          class="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-white/5 px-3 py-1.5 text-xs text-primary focus:outline-none focus:ring-2 focus:ring-[var(--primary)]">
          @for (opt of objectiveOptions; track opt.value) {
            <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>
        @if (manageBriefTabsService.focussedBrief()?.brief_goal_name) {
          <p class="text-xs text-secondary mt-1">
            Pre-selected from brief goal: <span class="italic">{{ manageBriefTabsService.focussedBrief()?.brief_goal_name }}</span>
          </p>
        }
      </div>

      @if (existingSparkAds().length > 0) {
        <p class="text-xs text-secondary mb-3">A Spark Ad already exists for this brief.</p>
      }

      <button
        type="button"
        (click)="createCampaignAndSparkAd()"
        [disabled]="!canCreateSparkAd()"
        class="px-4 py-2 bg-[#ff3b5c] text-white text-xs font-semibold rounded-lg hover:bg-[#e0334f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
        @if (isCreating()) {
          <div class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
          Creating…
        } @else {
          Create Campaign + Spark Ad
        }
      </button>
    </div>
  }

  <!-- ── Section 3: Existing Spark Ads ─────────────────────────────────── -->
  <div>
    <div class="flex flex-row items-center gap-x-4 w-full py-2 mb-3">
      <h3 class="text-sm font-semibold text-primary flex-nowrap min-w-fit">Existing Spark Ads</h3>
      <div class="border-b border-gray-200 dark:border-gray-800 w-full"></div>
    </div>

    @if (sparkAdsLoading()) {
      <div class="flex items-center justify-center py-6">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-[#ff3b5c]"></div>
      </div>
    } @else if (existingSparkAds().length === 0) {
      <div class="text-center py-6 bg-gray-50 dark:bg-white/5 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
        <p class="text-sm text-secondary">No Spark Ads created for this brief yet.</p>
      </div>
    } @else {
      <div class="space-y-3">
        @for (ad of existingSparkAds(); track ad.id) {
          <div class="briefing-section-card">
            <div class="flex items-center gap-2 mb-1">
              <h4 class="font-medium text-primary text-sm truncate">{{ ad.adName }}</h4>
              <span class="flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium"
                [class]="ad.operationStatus === 'ENABLE'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                  : ad.operationStatus === 'DISABLE'
                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                    : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'">
                {{ ad.operationStatus === 'ENABLE' ? 'Active' : ad.operationStatus === 'DISABLE' ? 'Paused' : 'Deleted' }}
              </span>
            </div>
            <div class="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-secondary font-mono">
              @if (ad.tiktokCampaignId) {
                <span>Campaign: {{ ad.tiktokCampaignId }}</span>
              }
              @if (ad.tiktokAdGroupId) {
                <span>Ad Group: {{ ad.tiktokAdGroupId }}</span>
              }
              @if (ad.tiktokAdId) {
                <span>Ad: {{ ad.tiktokAdId }}</span>
              }
            </div>
          </div>
        }
      </div>
    }
  </div>

</div>
